// utils/dateHelpers.ts
import {
    endOfDay,
    endOfMonth,
    endOfWeek,
    endOfYear,
    format,
    isValid,
    startOfDay,
    startOfMonth,
    startOfWeek,
    startOfYear,
    subDays,
    subMonths,
    subWeeks,
    subYears,
} from 'date-fns';
import type { DateRange } from '@/types';

/**
 * Ensures input is converted to a proper Date object
 * Handles string dates in YYYY-MM-DD format from API
 */
export const ensureDate = (date: Date | string | null | undefined): Date | null => {
    if (!date) return null;

    // If it's already a Date object, return it (if valid)
    if (date instanceof Date) {
        return isValid(date) ? date : null;
    }

    // Handle strings robustly
    if (typeof date === 'string') {
        // If it's a simple YYYY-MM-DD, append time to force local midnight
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            const d = new Date(date + 'T00:00:00');
            return isValid(d) ? d : null;
        }

        // Try parsing ISO/full date strings directly
        const parsed = new Date(date);
        return isValid(parsed) ? parsed : null;
    }

    // Unknown type
    return null;
};

/**
 * Creates a default date range (last 30 days)
 */
export const createDefaultRange = (): DateRange => ({
    startDate: subDays(new Date(), 30),
    endDate: new Date(),
});

/**
 * Normalizes a date range to ensure we have valid dates
 */
export const normalizeRange = (
    range: Partial<DateRange> | null | undefined,
    defaultRange: DateRange | null = null
): DateRange => {
    const fallback = defaultRange || createDefaultRange();

    if (!range) return fallback;

    const startDate = ensureDate(range.startDate) ?? fallback.startDate;
    const endDate = ensureDate(range.endDate) ?? fallback.endDate;

    return {
        startDate: isValid(startDate) ? startDate : fallback.startDate,
        endDate: isValid(endDate) ? endDate : fallback.endDate,
    };
};

/**
 * Formats a date for API consumption (YYYY-MM-DD)
 */
export const formatDateForApi = (date: Date | string | null | undefined): string | null => {
    if (!date) return null;
    const d = ensureDate(date);
    if (!d) return null;
    const normalizedDate = startOfDay(d);
    return format(normalizedDate, 'yyyy-MM-dd');
};

/**
 * Creates a compact display format for date ranges
 * e.g., "1-15 Jan 24" or "15 Dec 23-3 Jan 24"
 */
export const formatDateRangeDisplay = (range: Partial<DateRange> | null | undefined): string => {
    const normalizedRange = normalizeRange(range);

    const startDate = ensureDate(normalizedRange.startDate);
    const endDate = ensureDate(normalizedRange.endDate);

    if (!startDate || !endDate) return 'Invalid range';

    const startYear = format(startDate, 'yy');
    const endYear = format(endDate, 'yy');
    const startMonth = format(startDate, 'MMM');
    const endMonth = format(endDate, 'MMM');

    if (startYear === endYear) {
        if (startMonth === endMonth) {
            return `${format(startDate, 'd')}-${format(endDate, 'd')} ${startMonth} ${startYear}`;
        }
        return `${format(startDate, 'd MMM')}-${format(endDate, 'd MMM')} ${startYear}`;
    }

    return `${format(startDate, 'd MMM yy')}-${format(endDate, 'd MMM yy')}`;
};

export interface PresetRange {
    label: string;
    range: () => DateRange;
}

/**
 * Predefined date range generators
 */
export const createPresetRanges = (): PresetRange[] => [
    {
        label: 'Today',
        range: () => ({
            startDate: startOfDay(new Date()),
            endDate: endOfDay(new Date()),
        }),
    },
    {
        label: 'Yesterday',
        range: () => {
            const yesterday = subDays(new Date(), 1);
            return {
                startDate: startOfDay(yesterday),
                endDate: endOfDay(yesterday),
            };
        },
    },
    {
        label: 'This Week',
        range: () => ({
            startDate: startOfDay(startOfWeek(new Date(), { weekStartsOn: 1 })),
            endDate: endOfDay(endOfWeek(new Date(), { weekStartsOn: 1 })),
        }),
    },
    {
        label: 'Last Week',
        range: () => {
            const lastWeek = subWeeks(new Date(), 1);
            return {
                startDate: startOfDay(startOfWeek(lastWeek, { weekStartsOn: 1 })),
                endDate: endOfDay(endOfWeek(lastWeek, { weekStartsOn: 1 })),
            };
        },
    },
    {
        label: 'Last 7 Days',
        range: () => ({
            startDate: startOfDay(subDays(new Date(), 6)),
            endDate: endOfDay(new Date()),
        }),
    },
    {
        label: 'Last 30 Days',
        range: () => ({
            startDate: startOfDay(subDays(new Date(), 29)),
            endDate: endOfDay(new Date()),
        }),
    },
    {
        label: 'Last 90 Days',
        range: () => ({
            startDate: startOfDay(subDays(new Date(), 89)),
            endDate: endOfDay(new Date()),
        }),
    },
    {
        label: 'This Month',
        range: () => ({
            startDate: startOfDay(startOfMonth(new Date())),
            endDate: endOfDay(new Date()),
        }),
    },
    {
        label: 'Last Month',
        range: () => {
            const lastMonth = subMonths(new Date(), 1);
            return {
                startDate: startOfDay(startOfMonth(lastMonth)),
                endDate: endOfDay(endOfMonth(lastMonth)),
            };
        },
    },
    {
        label: 'This Year',
        range: () => ({
            startDate: startOfDay(startOfYear(new Date())),
            endDate: endOfDay(endOfYear(new Date())),
        }),
    },
    {
        label: 'This YTD',
        range: () => ({
            startDate: startOfDay(startOfYear(new Date())),
            endDate: endOfDay(new Date()),
        }),
    },
    {
        label: 'Last Year',
        range: () => {
            const lastYear = subYears(new Date(), 1);
            return {
                startDate: startOfDay(startOfYear(lastYear)),
                endDate: endOfDay(endOfYear(lastYear)),
            };
        },
    },
    {
        label: 'Last 1 Year',
        range: () => {
            const last1Year = subYears(new Date(), 1);
            return {
                startDate: startOfDay(last1Year),
                endDate: endOfDay(new Date()),
            };
        },
    },
];
